import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import * as Icons from "lucide-react";
import { Phone, Mail, Globe, MapPin, Send } from "lucide-react";
import { Logo } from "@/components/shared/Logo";
import { CONTACT, NAV_LINKS, SOCIALS } from "@/lib/site";

export const Footer = () => {
  const [email, setEmail] = useState("");

  const subscribe = (e) => {
    e.preventDefault();
    if (!/^\S+@\S+\.\S+$/.test(email)) {
      toast.error("Please enter a valid email address.");
      return;
    }
    setEmail("");
    toast.success("Thanks for subscribing! We'll keep you posted.");
  };

  return (
    <footer data-testid="site-footer" className="relative overflow-hidden bg-navy text-white">
      <div className="pointer-events-none absolute inset-x-0 bottom-0 select-none text-center">
        <span className="font-heading text-[26vw] font-bold leading-none text-white/[0.03]">ASAI</span>
      </div>

      <div className="container-asai relative z-10 py-16">
        {/* Newsletter band */}
        <div className="mb-14 flex flex-col gap-6 rounded-3xl border border-white/10 bg-white/[0.03] p-8 md:flex-row md:items-center md:justify-between">
          <div>
            <h3 className="font-heading text-2xl text-white">Stay in the loop</h3>
            <p className="mt-2 text-sm text-white/60">Program updates, expert sessions and internship openings — straight to your inbox.</p>
          </div>
          <form onSubmit={subscribe} noValidate data-testid="newsletter-form" className="flex w-full max-w-md gap-3">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              aria-label="Email address"
              data-testid="newsletter-email"
              className="w-full rounded-full border border-white/15 bg-white/5 px-5 py-3 text-sm text-white placeholder:text-white/40 outline-none transition-colors focus:border-orange"
            />
            <button
              type="submit"
              data-testid="newsletter-submit"
              className="inline-flex shrink-0 items-center gap-2 rounded-full bg-orange px-5 py-3 text-sm font-medium text-white transition-transform hover:scale-105"
            >
              <Send className="h-4 w-4" /> Subscribe
            </button>
          </form>
        </div>

        <div className="grid gap-12 lg:grid-cols-[1.4fr_1fr_1.2fr]">
          <div className="flex flex-col gap-5">
            <Logo />
            <p className="max-w-sm text-sm leading-relaxed text-white/60">{CONTACT.org}</p>
            <div className="text-sm text-white/50">
              <p>{CONTACT.initiative}</p>
              <p>{CONTACT.unit}</p>
            </div>
            <p className="max-w-xs font-heading text-lg text-white/80">
              Learn Today. <span className="text-orange">Lead Tomorrow.</span>
            </p>
            <div className="mt-2 flex gap-3" data-testid="footer-socials">
              {SOCIALS.map((s) => {
                const SIcon = Icons[s.icon] || Icons.Globe;
                return (
                  <a
                    key={s.label}
                    href={s.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={s.label}
                    data-testid={`social-${s.label.toLowerCase()}`}
                    className="flex h-10 w-10 items-center justify-center rounded-full border border-white/15 text-white/70 transition-colors hover:border-orange hover:text-orange"
                  >
                    <SIcon className="h-4 w-4" />
                  </a>
                );
              })}
            </div>
          </div>

          <div className="flex flex-col gap-4">
            <h4 className="text-xs font-semibold uppercase tracking-[0.2em] text-white/50">Explore</h4>
            <ul className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
              {NAV_LINKS.map((l) => {
                const key = l.to || l.href;
                const testId = `footer-nav-${(l.to || l.label).toLowerCase()}`;
                if (l.external) {
                  return (
                    <li key={key}>
                      <a
                        href={l.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={testId}
                        className="text-white/70 transition-colors hover:text-orange"
                      >
                        {l.label}
                      </a>
                    </li>
                  );
                }
                return (
                  <li key={key}>
                    <Link to={l.to} data-testid={testId} className="text-white/70 transition-colors hover:text-orange">
                      {l.label}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>

          <div className="flex flex-col gap-4">
            <h4 className="text-xs font-semibold uppercase tracking-[0.2em] text-white/50">Get in touch</h4>
            <a href={`tel:+${CONTACT.phoneRaw}`} className="flex items-center gap-3 text-sm text-white/75 hover:text-orange">
              <Phone className="h-4 w-4 text-orange" /> {CONTACT.phone}
            </a>
            <a href={`mailto:${CONTACT.email}`} className="flex items-center gap-3 text-sm text-white/75 hover:text-orange">
              <Mail className="h-4 w-4 text-orange" /> {CONTACT.email}
            </a>
            <span className="flex items-center gap-3 text-sm text-white/75">
              <Globe className="h-4 w-4 text-orange" /> {CONTACT.website}
            </span>
            <div className="flex gap-3 text-sm text-white/60">
              <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-orange" />
              <address className="not-italic leading-relaxed">
                {CONTACT.address.map((line) => (
                  <span key={line} className="block">{line}</span>
                ))}
              </address>
            </div>
          </div>
        </div>

        <div className="mt-14 flex flex-col items-center justify-between gap-3 border-t border-white/10 pt-6 text-xs text-white/40 sm:flex-row">
          <span>© {new Date().getFullYear()} Atmasamman Pvt. Ltd. All rights reserved.</span>
          <span>Learn Today. Lead Tomorrow.</span>
        </div>
      </div>
    </footer>
  );
};
